import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  HsButtonModule,
  HsCardModule,
  HsDividerModule,
} from '@hsetu/ngui-components';
import { HsLayerDirective } from '@hsetu/ngui-resources';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { PrintSettings } from '@shared/settings/print-settings.model';
import { KfmProcessType } from '@shared/enums/process-type.enum';
import { KfmAppShoppingCartColumns } from '@shared/enums/shopping-cart-columns.enum';
import { KaufmannPrintSettingsService } from '@shared/settings/print-settings.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'kfm-print-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HsLayerDirective,
    HsDividerModule,
    HsButtonModule,
    HsCardModule,
  ],
  templateUrl: './kfm-print-dialog.component.html',
})
export class KfmPrintDialogComponent {
  private readonly _settings = inject(KaufmannPrintSettingsService);
  private readonly _data = inject<{ processType: KfmProcessType }>(DIALOG_DATA);
  protected readonly dialogRef = inject<DialogRef<PrintSettings>>(
    DialogRef<PrintSettings>,
  );

  protected readonly KfmAppShoppingCartColumns = KfmAppShoppingCartColumns;

  protected get title(): string {
    return 'Druckeinstellungen';
  }

  protected get logoCardTitle(): string {
    return 'Logo';
  }

  protected get printLogoText(): string {
    return 'Firmenlogo drucken';
  }

  protected get addressCardTitle(): string {
    return 'Adressfelder';
  }

  protected get printSenderText(): string {
    return 'Absenderadresse drucken';
  }

  protected get printSenderLineText(): string {
    return 'Absenderadresse Zeile drucken';
  }

  protected get printCustomerText(): string {
    return 'Kundenadresse drucken';
  }

  protected get printPropertyAddressText(): string {
    return 'Liegenschaftsadresse drucken';
  }

  protected get parameterCardTitle(): string {
    return 'Formularparameter';
  }

  protected get printPretextText(): string {
    return 'Vortext drucken';
  }

  protected get printPostscriptText(): string {
    return 'Nachtext drucken';
  }

  protected get shoppingCartCardTitle(): string {
    return 'Spalten im Warenkorb anzeigen';
  }

  protected get printPositionNumberText(): string {
    return 'Positionsnummer';
  }

  protected get printQuantityText(): string {
    return 'Menge';
  }

  protected get printUnitText(): string {
    return 'Mengeneinheit';
  }

  protected get printArticleNumberText(): string {
    return 'Artikelnummer';
  }

  protected get printPerformanceText(): string {
    return 'Leistung';
  }

  protected get printSinglePriceText(): string {
    return 'Einzelpreis';
  }

  protected get printTotalPriceText(): string {
    return 'Gesamtpreis';
  }

  protected get printVatText(): string {
    return 'MwSt.';
  }

  protected printSettings: PrintSettings = {
    printSenderAddress: true,
    printSenderAddressLine: true,
    printClientLogo: true,
    printCustomerAddress: true,
    printPropertyAddress: true,
    printPreText: true,
    printPostText: true,
    displayShoppingCartColumns: [
      KfmAppShoppingCartColumns.PositionNumber,
      KfmAppShoppingCartColumns.Quantity,
      KfmAppShoppingCartColumns.UnitOfMeasure,
      KfmAppShoppingCartColumns.ItemNumber,
      KfmAppShoppingCartColumns.Performance,
      KfmAppShoppingCartColumns.Vat,
      KfmAppShoppingCartColumns.UnitPrice,
      KfmAppShoppingCartColumns.TotalPrice,
    ],
  };

  constructor() {
    if (this._data?.processType) {
      switch (this._data.processType) {
        case KfmProcessType.Offer:
          this.printSettings = { ...this._settings.printSettingsOffer };
          break;
        case KfmProcessType.Order:
          this.printSettings = { ...this._settings.printSettingsOrder };
          break;
        case KfmProcessType.Invoice:
          this.printSettings = { ...this._settings.printSettingsInvoice };
          break;
        default:
          console.warn('Unbekannter Prozess-Typ:', this._data.processType);
          break;
      }
    }
  }

  protected toggleColumn(
    column: KfmAppShoppingCartColumns,
    checked: boolean,
  ): void {
    const index = this.printSettings.displayShoppingCartColumns.indexOf(column);
    if (checked && index === -1) {
      this.printSettings.displayShoppingCartColumns.push(column);
    } else if (!checked && index > -1) {
      this.printSettings.displayShoppingCartColumns.splice(index, 1);
    }
  }

  protected isColumnSelected(column: KfmAppShoppingCartColumns): boolean {
    return this.printSettings.displayShoppingCartColumns.includes(column);
  }

  protected onOkClicked(): void {
    switch (this._data?.processType) {
      case KfmProcessType.Offer:
        this._settings.printSettingsOffer = { ...this.printSettings };
        break;
      case KfmProcessType.Order:
        this._settings.printSettingsOrder = { ...this.printSettings };
        break;
      case KfmProcessType.Invoice:
        this._settings.printSettingsInvoice = { ...this.printSettings };
        break;
    }

    this._settings.saveSettingsToStorage();
    this.dialogRef.close(
      this._settings.unmapPrintSettings(this.printSettings) as PrintSettings,
    );
  }

  protected onPropertyChanged(
    event: Event,
    propertyName: keyof PrintSettings,
  ): void {
    const input = event.target as HTMLInputElement;
    (this.printSettings[propertyName] as boolean) = input.checked;
  }
}
